import type { ReactiveDropdownDataSource, FormField, DropdownConfiguration } from '@fitogest/reactive-form-builder/interfaces';
import { ReactiveFormSerializers } from '@fitogest/reactive-form-builder/utils';
import type { ParcelasModel } from 'src/app/models/form-common/parcelas.model';
import type { ParcelasEnvironmentDefinition } from '../environment-variables';
import type { ParcelasFormDefinition } from './form-definition';

export const FINCA_RAW_IDENTITY_KEYS = ['id', 'id_finca', 'nombre', 'codigo_REA'] as const;
export const SECTOR_RAW_IDENTITY_KEYS = ['id', 'id_sector', 'id_finca', 'nombre'] as const;
export const COMUNIDAD_REGANTES_RAW_IDENTITY_KEYS = ['id', 'nombre'] as const;
export const ORIGEN_AGUA_RIEGO_RAW_IDENTITY_KEYS = ['codigo_SIEX', 'procedencia_del_agua_de_riego'] as const;
export const SISTEMA_RIEGO_RAW_IDENTITY_KEYS = ['codigo_SIEX', 'sistema_de_riego'] as const;
export const APLICADOR_RAW_IDENTITY_KEYS = ['id', 'nombre', 'apellidos', 'rol', 'tipo_carnet'] as const;
export const CULTIVO_RAW_IDENTITY_KEYS = ['catalogKey', 'catalogCode', 'id', 'codigo', 'codigo_cultivo', 'id_cultivo', 'cultivo'] as const;
export const VARIEDAD_RAW_IDENTITY_KEYS = [
    'catalogKey',
    'catalogCode',
    'id',
    'id_variedad',
    'codigo_variedad',
    'codigo_cultivo',
    'id_cultivo',
    'variedad',
] as const;

export type ParcelasDeclarativeDropdownDataSources = {
    readonly finca: () => ReactiveDropdownDataSource<ParcelasModel>;
    readonly sector: () => ReactiveDropdownDataSource<ParcelasModel>;
    readonly comunidadRegantes: () => ReactiveDropdownDataSource<ParcelasModel>;
    readonly origenAguaRiego: () => ReactiveDropdownDataSource<ParcelasModel>;
    readonly sistemaRiego: () => ReactiveDropdownDataSource<ParcelasModel>;
    readonly aplicadores: () => ReactiveDropdownDataSource<ParcelasModel>;
    readonly cultivoCatalog: () => ReactiveDropdownDataSource<ParcelasModel>;
    readonly variedadCatalog: () => ReactiveDropdownDataSource<ParcelasModel>;
};

export type ParcelasDeclarativeDropdownConfig = {
    readonly form: ParcelasFormDefinition;
    readonly formType: number;
    readonly env: ParcelasEnvironmentDefinition;
    readonly dataSources: ParcelasDeclarativeDropdownDataSources;
};

/**
 * Applies the declarative dropdown runtime wiring to the legacy Parcelas form definition.
 *
 * This is a transitional adapter: business components can keep their current Form class while the
 * field-level dataSource, dependencies and serializer metadata move closer to a standalone reactive definition.
 */
export function configureParcelasDeclarativeDropdowns(config: ParcelasDeclarativeDropdownConfig): void {
    if (config.formType >= 3) {
        return;
    }

    configureFincaSectorDropdownSources(config);
    configureParcelasSimpleDropdownSources(config);

    if (!config.env.activateApiSIEX) {
        return;
    }

    configureSiexCatalogDropdownSources(config);
}

function configureFincaSectorDropdownSources(config: ParcelasDeclarativeDropdownConfig): void {
    configureField(config.form, 'id_finca', (field) => {
        field.input.dropdownConfiguration = withDeclarativeDropdownConfiguration(
            field.input.dropdownConfiguration,
            {
                controlValueKey: 'id',
                backendValueKey: 'id',
                rawIdentityKeys: FINCA_RAW_IDENTITY_KEYS,
            },
        );
        field.dataSource = config.dataSources.finca();
    });

    configureField(config.form, 'id_sector', (field) => {
        field.vinculatedFields = undefined;
        field.dependencies = [];
        field.input.dropdownConfiguration = withDeclarativeDropdownConfiguration(
            field.input.dropdownConfiguration,
            {
                controlValueKey: 'id',
                backendValueKey: 'id',
                rawIdentityKeys: SECTOR_RAW_IDENTITY_KEYS,
            },
        );
        field.dataSource = config.dataSources.sector();
        field.dependencyBehaviour = {
            clearOnParentChange: true,
            clearOnHydration: false,
            filterLocally: true,
            keepValueUntilOptionsLoaded: true,
            preserveValueWhenOptionsMissing: true,
            warnWhenSelectedValueMissing: true,
        };
    });
}

function configureParcelasSimpleDropdownSources(config: ParcelasDeclarativeDropdownConfig): void {
    configureField(config.form, 'id_comunidad_regantes', (field) => {
        field.input.dropdownConfiguration = withDeclarativeDropdownConfiguration(
            field.input.dropdownConfiguration,
            {
                controlValueKey: 'id',
                backendValueKey: 'id',
                rawIdentityKeys: COMUNIDAD_REGANTES_RAW_IDENTITY_KEYS,
            },
        );
        field.dataSource = config.dataSources.comunidadRegantes();
    });

    configureField(config.form, 'codigo_procedencia_agua', (field) => {
        field.input.dropdownConfiguration = withDeclarativeDropdownConfiguration(
            field.input.dropdownConfiguration,
            {
                controlValueKey: 'codigo_SIEX',
                backendValueKey: 'codigo_SIEX',
                rawIdentityKeys: ORIGEN_AGUA_RIEGO_RAW_IDENTITY_KEYS,
            },
        );
        field.dataSource = config.dataSources.origenAguaRiego();
    });

    configureField(config.form, 'codigo_sistema_riego', (field) => {
        field.input.dropdownConfiguration = withDeclarativeDropdownConfiguration(
            field.input.dropdownConfiguration,
            {
                controlValueKey: 'codigo_SIEX',
                backendValueKey: 'codigo_SIEX',
                rawIdentityKeys: SISTEMA_RIEGO_RAW_IDENTITY_KEYS,
            },
        );
        field.dataSource = config.dataSources.sistemaRiego();
        field.dependencyBehaviour = {
            clearOnParentChange: true,
            clearOnHydration: false,
            filterLocally: true,
            keepValueUntilOptionsLoaded: true,
            preserveValueWhenOptionsMissing: true,
            warnWhenSelectedValueMissing: true,
        };
    });

    if (config.env.appName === 'sequiador' || config.env.appName === 'avafitogest') {
        configureField(config.form, 'aplicadores', (field) => {
            field.input.dropdownConfiguration = withDeclarativeDropdownConfiguration(
                field.input.dropdownConfiguration,
                {
                    controlValueKey: 'id',
                    backendValueKey: 'id',
                    rawIdentityKeys: APLICADOR_RAW_IDENTITY_KEYS,
                },
            );
            field.dataSource = config.dataSources.aplicadores();
        });
    }
}

function configureSiexCatalogDropdownSources(config: ParcelasDeclarativeDropdownConfig): void {
    configureField(config.form, 'id_cultivo', (field) => {
        field.input.dropdownConfiguration = withDeclarativeDropdownConfiguration(
            field.input.dropdownConfiguration,
            {
                controlValueKey: 'catalogKey',
                backendValueKey: 'codigo',
                rawIdentityKeys: CULTIVO_RAW_IDENTITY_KEYS,
            },
        );
        field.dataSource = config.dataSources.cultivoCatalog();
    });

    configureField(config.form, 'id_variedad', (field) => {
        field.input.dropdownConfiguration = withDeclarativeDropdownConfiguration(
            field.input.dropdownConfiguration,
            {
                controlValueKey: 'catalogKey',
                backendValueKey: 'codigo_variedad',
                rawIdentityKeys: VARIEDAD_RAW_IDENTITY_KEYS,
            },
        );
        field.dataSource = config.dataSources.variedadCatalog();
        field.vinculatedFields = ['id_cultivo'];
        field.dependencyBehaviour = {
            clearOnParentChange: true,
            clearOnHydration: false,
            preserveValueWhenOptionsMissing: true,
            warnWhenSelectedValueMissing: true,
            reloadOnParentChange: true,
            autoSelect: 'singleOption',
        };
    });

    config.form.formFields.serializer = {
        ...config.form.formFields.serializer,
        fields: {
            ...config.form.formFields.serializer?.fields,
            id_cultivo: ReactiveFormSerializers.catalogBackendValue(),
            id_variedad: ReactiveFormSerializers.catalogBackendValue(),
        },
    };
}

function configureField(
    form: ParcelasFormDefinition,
    fieldName: string,
    configure: (field: FormField<ParcelasModel>) => void,
): void {
    for (const section of form.formFields.sections ?? []) {
        for (const field of section.fields ?? []) {
            if (field.name === fieldName) {
                configure(field as FormField<ParcelasModel>);
                return;
            }
        }
    }
}

function withDeclarativeDropdownConfiguration(
    current: DropdownConfiguration<ParcelasModel> | undefined,
    configuration: DropdownConfiguration<ParcelasModel>,
): DropdownConfiguration<ParcelasModel> {
    const currentWithoutLegacyFilter = { ...(current ?? {}) };
    delete currentWithoutLegacyFilter.filter;

    return {
        ...currentWithoutLegacyFilter,
        ...configuration,
    };
}
